import { Check, Flame, X } from 'lucide-react';
import { motion } from 'motion/react';
import { type FC, type ReactNode, useState } from 'react';
import classNames from 'classnames';

export interface HotOfferCardProps {
  title: string;
  description: string;
  /** Текст основной кнопки (если не передан — будет "Принять") */
  primaryLabel?: string;
  /** Текст вторичной кнопки (если не передан — будет "Позже") */
  secondaryLabel?: string;
  /** Обработчик основной кнопки (deprecated, оставлено для совместимости) */
  onPrimaryAction?: () => void;
  /** Обработчик основной кнопки */
  onAccept?: () => void;
  onDismiss?: () => void;
  /** Иконка слева от заголовка (по умолчанию — Flame из lucide) */
  icon?: ReactNode;
  className?: string;
}

export const HotOfferCard: FC<HotOfferCardProps> = ({
  title,
  description,
  onAccept,
  primaryLabel,
  secondaryLabel,
  onPrimaryAction,
  onDismiss,
  icon,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 300);
  };

  const handleAccept = () => {
    onAccept?.();
    onPrimaryAction?.();
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={classNames(
        'relative rounded-xl border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-red-50 p-6 shadow-lg',
        className,
      )}
    >
      <motion.div
        className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-100 to-red-100 opacity-0"
        animate={{
          opacity: [0, 0.3, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 transition-colors hover:text-gray-600"
        aria-label="Закрыть"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="relative space-y-4">
        <div className="flex items-start gap-3">
          <motion.div
            animate={{
              rotate: [0, 10, -10, 10, 0],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatDelay: 2,
            }}
            className="shrink-0 text-orange-500"
            aria-hidden
          >
            {icon ?? <Flame className="h-6 w-6" />}
          </motion.div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="mt-2 leading-relaxed text-gray-700">{description}</p>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleAccept}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 font-medium text-white shadow-md transition-all duration-200 hover:bg-red-700 hover:shadow-lg"
          >
            <Check className="h-5 w-5 shrink-0" />
            {primaryLabel ?? 'Принять'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-100"
          >
            {secondaryLabel ?? 'Позже'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};
